import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Alert } from 'react-bootstrap';
import { authService } from '../services/api';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import NavbarBrandLogo from './NavbarBrandLogo';
import './LoginPage.css';

interface LoginPageProps {
  onLoginSuccess?: () => void;
}

const LoginPage: React.FC<LoginPageProps> = ({ onLoginSuccess }) => {
  const { t } = useSafeTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  useEffect(() => {
    const checkAuthStatus = async () => {
      const token = localStorage.getItem('authToken');
      
      if (token) {
        try {
          // Try to validate the token
          await authService.validateToken();
          // If successful and no onLoginSuccess callback, redirect to home
          if (!onLoginSuccess) {
            window.location.href = '/';
            return;
          }
        } catch (error) {
          // If token is invalid, remove it and show login page
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          localStorage.removeItem('userId');
        }
      }
      
      setIsCheckingAuth(false);
    };

    checkAuthStatus();
  }, [onLoginSuccess]);

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    setError(null);

    try {
      authService.googleLogin();
      // If onLoginSuccess is provided, call it after successful login
      if (onLoginSuccess) {
        onLoginSuccess();
      }
    } catch (err) {
      setError(t('auth.loginErrorGoogle', 'Error trying to login with Google. Please try again.'));
      setIsLoading(false);
    }
  };

  // Show loading while checking authentication
  if (isCheckingAuth) {
    if (onLoginSuccess) {
      return (
        <div className="text-center p-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading', 'Loading...')}</span>
          </div>
          <p className="mt-3 text-muted">{t('auth.checkingAuth', 'Checking authentication...')}</p>
        </div>
      );
    }
    
    return (
      <div className="login-page">
        <Container fluid className="h-100">
          <Row className="h-100 align-items-center justify-content-center">
            <Col xs={12} sm={8} md={6} lg={4} xl={3}>
              <Card className="login-card shadow">
                <Card.Body className="text-center p-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">{t('common.loading', 'Loading...')}</span>
                  </div>
                  <p className="mt-3 text-muted">{t('auth.checkingAuth', 'Checking authentication...')}</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  // Render as modal content if onLoginSuccess is provided
  if (onLoginSuccess) {
    return (
      <div className="login-modal-content">
        <div className="login-brand-block mb-4">
          <div className="login-brand-logo-row">
            <NavbarBrandLogo />
          </div>
          <div className="login-brand-name">Quizter Club</div>
          <p className="login-brand-subtitle text-muted mb-0">
            {t('common.welcomeSubtitle', 'Learn and have fun with interactive quizzes!')}
          </p>
        </div>

        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}

        <div className="login-options">
          <Button
            variant="outline-primary"
            size="lg"
            className="google-login-btn w-100 mb-3"
            onClick={handleGoogleLogin}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                {t('auth.connecting', 'Connecting...')}
              </>
            ) : (
              <>
                <svg className="google-icon me-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {t('auth.signInWithGoogle', 'Sign in with Google')}
              </>
            )}
          </Button>

          <div className="features mt-4">
            <div className="feature-item">
              <i className="bi bi-trophy text-warning"></i>
              <span>{t('auth.featurePoints', 'Earn points and level up')}</span>
            </div>
            <div className="feature-item">
              <i className="bi bi-people text-info"></i>
              <span>{t('auth.featureCompete', 'Compete with other players')}</span>
            </div>
            <div className="feature-item">
              <i className="bi bi-graph-up text-success"></i>
              <span>{t('auth.featureProgress', 'Track your progress')}</span>
            </div>
          </div>

          <div className="mt-4">
            <small className="text-muted">
              {t('auth.termsNotice', 'By continuing, you agree to our Terms of Service and Privacy Policy.')}
            </small>
          </div>
        </div>
      </div>
    );
  }

  // Full page login
  return (
    <div className="login-page">
      <Container fluid className="h-100">
        <Row className="h-100 align-items-center justify-content-center">
          <Col xs={12} sm={8} md={6} lg={4} xl={3}>
            <Card className="login-card shadow">
              <Card.Body className="text-center p-4">
                <div className="login-brand-block mb-4">
                  <div className="login-brand-logo-row">
                    <NavbarBrandLogo />
                  </div>
                  <div className="login-brand-name">Quizter Club</div>
                  <p className="login-brand-subtitle text-muted mb-0">
                    {t('common.welcomeSubtitle', 'Learn and have fun with interactive quizzes!')}
                  </p>
                </div>

                {error && (
                  <Alert variant="danger" className="mb-3">
                    {error}
                  </Alert>
                )}

                <div className="login-options">
                  <Button
                    variant="outline-primary"
                    size="lg"
                    className="google-login-btn w-100 mb-3"
                    onClick={handleGoogleLogin}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {t('auth.connecting', 'Connecting...')}
                      </>
                    ) : (
                      <>
                        <svg className="google-icon me-2" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        {t('auth.signInWithGoogle', 'Sign in with Google')}
                      </>
                    )}
                  </Button>

                  <div className="features mt-4">
                    <div className="feature-item">
                      <i className="bi bi-trophy text-warning"></i>
                      <span>{t('auth.featurePoints', 'Earn points and level up')}</span>
                    </div>
                    <div className="feature-item">
                      <i className="bi bi-people text-info"></i>
                      <span>{t('auth.featureCompete', 'Compete with other players')}</span>
                    </div>
                    <div className="feature-item">
                      <i className="bi bi-graph-up text-success"></i>
                      <span>{t('auth.featureProgress', 'Track your progress')}</span>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default LoginPage; 